import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/apiService';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalWorkOrders: 0,
    activeWorkOrders: 0,
    totalRevenue: 0,
    totalInvoices: 0,
    pendingInvoices: 0,
    lowStockProducts: 0,
    totalCustomers: 0,
    monthlyRevenue: 0,
    monthlyOrders: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [recentActivities, setRecentActivities] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Gerçek API çağrıları
      const [statsResponse, activitiesResponse, productsResponse, revenueResponse] = await Promise.all([
        dashboardAPI.getStats(selectedPeriod),
        dashboardAPI.getRecentActivities(10),
        dashboardAPI.getTopProducts(selectedPeriod, 5),
        dashboardAPI.getRevenueChart(selectedPeriod)
      ]);

      setStats(statsResponse.data);
      setRecentActivities(activitiesResponse.data);
      setTopProducts(productsResponse.data);
      setRevenueData(revenueResponse.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Dashboard verileri yüklenirken hata:', error);
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    // Bu fonksiyonlar ilgili sayfalara yönlendirme yapacak
    console.log('Hızlı işlem:', action);
    // Router ile yönlendirme yapılabilir
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'product': return 'blue';
      case 'workorder': return 'green';
      case 'user': return 'purple';
      case 'order': return 'orange';
      case 'invoice': return 'red';
      case 'stock': return 'yellow';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Dashboard yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Firma Yönetim Paneli</h2>
          <p>Hoş geldiniz! Firma yönetimi için hızlı erişim ve genel bakış</p>
        </div>
        <div className="header-actions">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="period-select"
          >
            <option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option>
            <option value="quarter">Bu Çeyrek</option>
            <option value="year">Bu Yıl</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>₺{(stats.totalRevenue || 0).toLocaleString()}</h3>
            <p>Toplam Gelir</p>
            <span className="stat-change positive">+12.5%</span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats.totalProducts || 0}</h3>
            <p>Toplam Ürün</p>
            <span className="stat-change positive">+8.3%</span>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>{stats.totalOrders || 0}</h3>
            <p>Toplam Sipariş</p>
            <span className="stat-change positive">+15.2%</span>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{stats.totalCustomers || 0}</h3>
            <p>Toplam Müşteri</p>
            <span className="stat-change positive">+5.7%</span>
          </div>
        </div>

        <div className="stat-card danger">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <h3>{stats.pendingInvoices || 0}</h3>
            <p>Bekleyen Fatura</p>
            <span className="stat-change negative">+2.1%</span>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{stats.lowStockProducts || 0}</h3>
            <p>Düşük Stok</p>
            <span className="stat-change neutral">0%</span>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="content-left">
          <div className="quick-actions">
            <h3>Hızlı İşlemler</h3>
            <div className="actions-grid">
              <button 
                className="action-btn primary" 
                onClick={() => handleQuickAction('addProduct')}
              >
                <span className="action-icon">➕</span>
                <span>Yeni Ürün Ekle</span>
              </button>
              <button 
                className="action-btn success" 
                onClick={() => handleQuickAction('createInvoice')}
              >
                <span className="action-icon">📄</span>
                <span>Fatura Oluştur</span>
              </button>
              <button 
                className="action-btn warning" 
                onClick={() => handleQuickAction('createOrder')}
              >
                <span className="action-icon">📋</span>
                <span>Yeni Sipariş</span>
              </button>
              <button 
                className="action-btn info" 
                onClick={() => handleQuickAction('createWorkOrder')}
              >
                <span className="action-icon">🔧</span>
                <span>İş Emri Oluştur</span>
              </button>
              <button 
                className="action-btn purple" 
                onClick={() => handleQuickAction('addUser')}
              >
                <span className="action-icon">👥</span>
                <span>Kullanıcı Ekle</span>
              </button>
              <button 
                className="action-btn secondary" 
                onClick={() => handleQuickAction('viewReports')}
              >
                <span className="action-icon">📊</span>
                <span>Rapor Görüntüle</span>
              </button>
            </div>
          </div>

          <div className="revenue-chart">
            <h3>Aylık Gelir Grafiği</h3>
            <div className="chart-container">
              <div className="chart-bars">
                {revenueData.map((data, index) => (
                  <div key={index} className="chart-bar">
                    <div 
                      className="bar-fill" 
                      style={{ height: `${(data.revenue / 52000) * 100}%` }}
                    ></div>
                    <span className="bar-label">{data.month}</span>
                  </div>
                ))}
              </div>
              <div className="chart-labels">
                <span>₺0</span>
                <span>₺52,000</span>
              </div>
            </div>
          </div>
        </div>

        <div className="content-right">
          <div className="top-products">
            <h3>En Çok Satan Ürünler</h3>
            <div className="products-list">
              {topProducts.map((product, index) => (
                <div key={product.id} className="product-item">
                  <div className="product-rank">#{index + 1}</div>
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <p>{product.sales} satış • ₺{product.revenue}</p>
                  </div>
                  <div className="product-revenue">
                    ₺{product.revenue}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="recent-activity">
            <h3>Son Aktiviteler</h3>
            <div className="activity-list">
              {recentActivities.map(activity => (
                <div key={activity.id} className={`activity-item ${getStatusColor(activity.type)}`}>
                  <div className="activity-icon">{activity.icon}</div>
                  <div className="activity-content">
                    <p>{activity.message}</p>
                    <div className="activity-meta">
                      <span className="activity-time">{activity.time}</span>
                      <span className="activity-user">{activity.user}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <span className="stat-label">Aktif İş Emirleri:</span>
            <span className="stat-value">{stats.activeWorkOrders}</span>
          </div>
          <div className="footer-stat">
            <span className="stat-label">Bu Ay Gelir:</span>
            <span className="stat-value">₺{stats.monthlyRevenue.toLocaleString()}</span>
          </div>
          <div className="footer-stat">
            <span className="stat-label">Bu Ay Sipariş:</span>
            <span className="stat-value">{stats.monthlyOrders}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 